import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/App";
import {
  TrendingUp, Users, BarChart3, FileText, Rocket,
  CheckCircle, Clock, ChevronRight, ArrowUpRight
} from "lucide-react";

const TOOL_LABELS = {
  snapshot: "Health Snapshot",
  kpi_scorecard: "KPI Scorecard",
  tech_stack: "Tech Stack",
  marketing_strategy: "Marketing Strategy",
  business_plan: "Business Plan",
  implementation_plan: "Implementation Plan",
  org_map: "Org Map",
  time_budget: "Time Budget",
};

const ClientProgressPanel = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);

  useEffect(() => {
    if (open) fetchProgress();
  }, [open]);

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/client-progress`);
      if (res.ok) setData(await res.json());
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="client-progress-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> Client Progress Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Clock className="w-5 h-5 animate-spin text-gray-400 mr-2" /> Loading...
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4 text-center">
                  <Users className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-800">{data.total_clients}</p>
                  <p className="text-xs text-emerald-600">Total Clients</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4 text-center">
                  <BarChart3 className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-800">{data.total_tools_generated}</p>
                  <p className="text-xs text-blue-600">Tools Generated</p>
                </CardContent>
              </Card>
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4 text-center">
                  <Rocket className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-purple-800">{data.total_engagements_enrolled}</p>
                  <p className="text-xs text-purple-600">Engagements</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4 text-center">
                  <FileText className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-800">{data.total_sops_created}</p>
                  <p className="text-xs text-amber-600">SOPs Created</p>
                </CardContent>
              </Card>
            </div>

            {/* Client List */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Client Activity ({data.clients.length})</h4>
              {data.clients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No clients yet. Invite clients to start tracking progress.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.clients.map((client) => {
                    const isExpanded = expandedClient === client.user_id;
                    const activityScore = client.tools_count + client.engagements_enrolled + client.weekly_logs;
                    const activityLevel = activityScore >= 10 ? "High" : activityScore >= 3 ? "Medium" : "Low";
                    const activityColor = activityScore >= 10 ? "emerald" : activityScore >= 3 ? "blue" : "gray";

                    return (
                      <Card
                        key={client.user_id}
                        className="cursor-pointer hover:shadow-sm transition-all"
                        onClick={() => setExpandedClient(isExpanded ? null : client.user_id)}
                        data-testid={`client-row-${client.user_id}`}
                      >
                        <CardContent className="p-0">
                          <div className="flex items-center gap-3 p-3">
                            <div className={`w-10 h-10 rounded-full bg-${activityColor}-100 flex items-center justify-center flex-shrink-0`}>
                              <span className={`font-semibold text-sm text-${activityColor}-700`}>
                                {(client.name || "?").charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm text-gray-900 truncate">{client.name || client.email}</p>
                                <Badge variant="outline" className="text-xs">{client.tier || "basic"}</Badge>
                                <Badge className={`text-xs bg-${activityColor}-100 text-${activityColor}-700`}>{activityLevel}</Badge>
                              </div>
                              <p className="text-xs text-gray-500 truncate">{client.business_name || client.email}</p>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                              <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {client.tools_count}</span>
                              <span className="flex items-center gap-1"><Rocket className="w-3 h-3" /> {client.engagements_enrolled}</span>
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {client.sops_created}</span>
                              <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t bg-gray-50 p-4">
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs font-semibold text-gray-600 mb-2">Scaling Tools Used</p>
                                  {client.tools_used.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {client.tools_used.map((t, i) => (
                                        <Badge key={i} className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                                          <CheckCircle className="w-3 h-3 mr-1" /> {TOOL_LABELS[t] || t}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">No tools used yet</p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-gray-600 mb-2">Engagement Status</p>
                                  {client.engagement_details.length > 0 ? (
                                    <div className="space-y-1">
                                      {client.engagement_details.map((e, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs">
                                          <ArrowUpRight className="w-3 h-3 text-blue-500" />
                                          <span className="text-gray-700">{e.engagement_id?.replace(/_/g, " ")}</span>
                                          <Badge variant="outline" className="text-xs">{e.status}</Badge>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">No engagements yet</p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-gray-600 mb-2">Activity</p>
                                  <div className="space-y-1 text-xs text-gray-600">
                                    <p>{client.weekly_logs} weekly logs submitted</p>
                                    <p>{client.projects} active projects</p>
                                    <p>{client.sops_created} SOPs created</p>
                                    {client.joined && <p className="text-gray-400">Joined: {new Date(client.joined).toLocaleDateString()}</p>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">Failed to load data</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientProgressPanel;
